import React from 'react'

/**
 * A hook which handles managing all step-related state/logic. Accepts an array
 * of steps and returns an object containing information about the current steps
 * state, some navigation functions, as well as the current active step/element
 * wrapped in a provider to make everything available to the step via context
 *
 * @param steps - An array of JSX elements, aka. the views which are the steps
 */

type Options = {
    onStepChange?: () => void
}

export const useStepWizard = (steps: JSX.Element[], options: Options = {}) => {
    const [activeStepIndex, setActiveStepIndex] = React.useState(0)
    const totalSteps = steps.length
    const activeStepElement = steps[activeStepIndex]

    const goToStep = (step: number) => {
        if (step >= 0 && step < totalSteps) {
            setActiveStepIndex(step)
            options.onStepChange?.()
        }
    }

    const navigate = {
        next: () => goToStep(activeStepIndex + 1),
        prev: () => goToStep(activeStepIndex - 1),
        goToStep,
    }

    return {
        activeStepIndex,
        navigate,
        totalSteps,
        activeStepElement: (
            <StepsContext.Provider
                value={{
                    activeStepIndex,
                    navigate,
                    totalSteps,
                }}
            >
                {activeStepElement}
            </StepsContext.Provider>
        ),
    }
}

interface Navigate {
    next: () => void
    prev: () => void
    goToStep: (step: number) => void
}

interface Context {
    activeStepIndex: number
    totalSteps: number
    navigate: Navigate
}

const StepsContext = React.createContext<Context>({
    activeStepIndex: 0,
    totalSteps: 0,
    navigate: {
        next: () => {},
        prev: () => {},
        goToStep: () => {},
    },
})

export const useWizardContext = () => React.useContext(StepsContext)
